import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

import { Button } from "./button";

type TPanel = {
  children: React.ReactNode;
  isOpen: boolean;
  setIsOpen: (showPanel: boolean) => void;
  title: string;
  trigger: () => JSX.Element;
  /** Optional DOM element id to portal the panel into (should be a positioned container) */
  containerId?: string;
};

const Panel = ({ children, isOpen, setIsOpen, title, trigger, containerId }: TPanel) => {
  const containerEl = typeof document !== 'undefined' && containerId ? document.getElementById(containerId) : null;

  const positionClasses = containerEl ? "absolute right-0 top-0 bottom-0" : "fixed right-0 top-0 bottom-0";
  const sizeClasses = containerEl ? "w-full max-w-[33%]" : "w-full sm:w-96";

  const panelNode = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: "100%" }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: "100%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={`${positionClasses} z-40 pb-4 bg-white shadow-lg ${sizeClasses} flex h-full flex-col overflow-y-auto`}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 pt-2">
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 px-4 py-2">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div>
      <div title={title}>{trigger()}</div>
      {containerEl ? createPortal(panelNode, containerEl) : panelNode}
    </div>
  );
};

export default Panel;
